/**
 * WVT2032Watch.tsx — Public tournament hub for WVT 2032 McFarland
 *
 * URL: /WVT2032   (no login required)
 *
 * Each game card fetches live state from the public endpoint and shows:
 *   • Upcoming  → date / time / location
 *   • Live      → "Watch Live" button (red pulse)
 *   • Completed → "Watch Replay" if a recording exists, else "Replay coming soon…"
 *
 * Polls every 30 s so cards flip live automatically.
 */
import React, { useEffect, useState } from 'react';
import { mediaUrl } from '../utils/mediaUrl';

// ── Team branding ─────────────────────────────────────────────────────────────
const NAVY   = '#012f7b';
const ACCENT = '#4a90e2'; // lighter blue for highlights
const BG     = '#04070f';

// ── Game definitions (IDs from DB) ──────────────────────────────────────────
const GAMES = [
  {
    id:       '16b1a38d-b792-477d-9987-ddb373e2d786',
    label:    'Game 1',
    date:     'Saturday, June 27',
    time:     '10:00 AM ET',
    opponent: 'Next Girls Basketball 2032',
    homeAway: 'away' as const,
  },
  {
    id:       '4970ad84-645a-4cb6-9de6-04eb621d28be',
    label:    'Game 2',
    date:     'Saturday, June 27',
    time:     '2:20 PM ET',
    opponent: 'MBA Future 2032',
    homeAway: 'home' as const,
  },
  {
    id:       '7f751c0e-7d6e-4d02-8022-f145c3764871',
    label:    'Game 3',
    date:     'Sunday, June 28',
    time:     'Time TBD',
    opponent: 'TBD',
    homeAway: 'away' as const,
  },
];

interface GameState {
  status:     string;
  home_score?: number;
  away_score?: number;
  recording?: {
    id:                 string;
    file_path:          string;
    compressed_path?:   string | null;
    duration_seconds?:  number | null;
  } | null;
}

// ── App Store badge ───────────────────────────────────────────────────────────
const AppStoreBadge: React.FC = () => (
  <a
    href="https://apps.apple.com/us/app/slow-rib-sports/id6760488944"
    target="_blank"
    rel="noopener noreferrer"
    style={{ display: 'inline-block', lineHeight: 0 }}
  >
    <svg width="140" height="42" viewBox="0 0 140 42" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="140" height="42" rx="7" fill="#000"/>
      <rect width="140" height="42" rx="7" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="0.8"/>
      <path d="M21.5 10.5c1.2-1.4 2-3.3 1.8-5.2-1.7.1-3.8 1.2-5 2.6-1.1 1.2-2 3.2-1.8 5 2 .2 3.9-.9 5-2.4z" fill="white"/>
      <path d="M23.3 13.3c-2.8-.2-5.1 1.6-6.4 1.6-1.3 0-3.3-1.5-5.4-1.5-2.8.1-5.4 1.7-6.8 4.2-2.9 5-.8 12.4 2 16.5 1.4 2 3 4.2 5.2 4.1 2-.1 2.8-1.3 5.2-1.3 2.4 0 3.1 1.3 5.2 1.3 2.2 0 3.7-2 5-4 1.6-2.3 2.2-4.5 2.3-4.6-.1 0-4.4-1.7-4.4-6.6 0-4.2 3.4-6.2 3.6-6.3-2-2.9-5-3.4-5.5-3.4z" fill="white"/>
      <text x="36" y="16" fill="white" fontFamily="-apple-system, Helvetica, Arial, sans-serif" fontSize="8" fontWeight="400" letterSpacing="0.2">Download on the</text>
      <text x="35" y="30" fill="white" fontFamily="-apple-system, Helvetica, Arial, sans-serif" fontSize="17" fontWeight="600" letterSpacing="-0.3">App Store</text>
    </svg>
  </a>
);

// ── Single game card ──────────────────────────────────────────────────────────
const GameCard: React.FC<{ game: typeof GAMES[number]; index: number }> = ({ game, index }) => {
  const [state,      setState]      = useState<GameState | null>(null);
  const [loading,    setLoading]    = useState(true);
  const [showPlayer, setShowPlayer] = useState(false);

  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_URL ?? '/api';
    const fetchState = () =>
      fetch(`${API_BASE}/games/${game.id}/public`)
        .then(r => r.ok ? r.json() : null)
        .then((data: any) => {
          if (!data) return;
          setState({
            status:     data.status,
            home_score: data.score?.home_score,
            away_score: data.score?.away_score,
            recording:  data.recording ?? null,
          });
        })
        .catch(() => {})
        .finally(() => setLoading(false));

    fetchState();
    const interval = setInterval(fetchState, 30_000);
    return () => clearInterval(interval);
  }, [game.id]);

  const isLive        = state?.status === 'live';
  const isCompleted   = state?.status === 'completed';
  const hasRecording  = isCompleted && !!state?.recording?.file_path;

  const recordingUrl = hasRecording ? (() => {
    const HLS_BASE = process.env.REACT_APP_HLS_URL ?? '/hls';
    const fp = state!.recording!.compressed_path || state!.recording!.file_path;
    return (fp.startsWith('/api/') || fp.startsWith('http')) ? fp : `${HLS_BASE}${fp}`;
  })() : null;

  const scoreDisplay =
    isCompleted && state?.home_score != null && state?.away_score != null
      ? `${state.home_score}–${state.away_score}`
      : null;

  const borderColor = isLive
    ? 'rgba(239,68,68,0.5)'
    : isCompleted
      ? 'rgba(74,144,226,0.25)'
      : 'rgba(255,255,255,0.08)';

  return (
    <div style={{
      background:   'rgba(255,255,255,0.03)',
      border:       `1px solid ${borderColor}`,
      borderRadius: 14,
      overflow:     'hidden',
      transition:   'border-color 0.2s',
    }}>
      {/* ── Header row ── */}
      <div style={{
        padding:      '16px 20px',
        display:      'flex',
        alignItems:   'center',
        gap:          14,
        borderBottom: isLive || hasRecording ? '1px solid rgba(255,255,255,0.06)' : 'none',
      }}>

        {/* Game badge */}
        <div style={{
          width:          44,
          height:         44,
          borderRadius:   10,
          background:     isLive ? '#EF4444' : NAVY,
          display:        'flex',
          flexDirection:  'column',
          alignItems:     'center',
          justifyContent: 'center',
          flexShrink:     0,
          position:       'relative',
        }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Game</span>
          <span style={{ fontSize: 16, fontWeight: 900, color: '#fff', lineHeight: 1 }}>{index + 1}</span>
          {isLive && (
            <div style={{
              position: 'absolute', top: -4, right: -4,
              width: 10, height: 10, borderRadius: '50%',
              background: '#EF4444', border: `2px solid ${BG}`,
              animation: 'wvt-pulse 1.5s infinite',
            }} />
          )}
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' as const }}>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>
              {game.date} · {game.time}
            </span>
            {!loading && isLive && (
              <span style={{ fontSize: 9, fontWeight: 800, color: '#EF4444', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 20, padding: '2px 7px', letterSpacing: '0.8px', textTransform: 'uppercase' as const }}>
                LIVE
              </span>
            )}
            {!loading && isCompleted && (
              <span style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '2px 7px', letterSpacing: '0.5px', textTransform: 'uppercase' as const }}>
                FINAL{scoreDisplay ? ` · ${scoreDisplay}` : ''}
              </span>
            )}
          </div>

          <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
            {game.homeAway === 'away'
              ? <>WVT 2032 <span style={{ color: 'rgba(255,255,255,0.35)', fontWeight: 400, fontSize: 13 }}>@</span> {game.opponent}</>
              : <>WVT 2032 <span style={{ color: 'rgba(255,255,255,0.35)', fontWeight: 400, fontSize: 13 }}>vs.</span> {game.opponent}</>
            }
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div style={{ padding: '12px 20px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Live or upcoming → stream link */}
        {!isCompleted && (
          <a
            href={`/live/${game.id}`}
            style={{
              display:        'inline-flex',
              alignItems:     'center',
              gap:            8,
              padding:        '10px 20px',
              background:     isLive ? '#EF4444' : 'rgba(1,47,123,0.6)',
              border:         isLive ? 'none' : `1px solid rgba(74,144,226,0.3)`,
              borderRadius:   8,
              color:          '#fff',
              fontWeight:     800,
              fontSize:       14,
              textDecoration: 'none',
              letterSpacing:  '-0.2px',
              whiteSpace:     'nowrap' as const,
            }}
          >
            {isLive
              ? <><div style={{ width: 7, height: 7, borderRadius: '50%', background: 'rgba(255,255,255,0.8)', animation: 'wvt-pulse 1.5s infinite' }} /> Watch Live</>
              : <><svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="3,2 12,7 3,12" fill="currentColor" stroke="none"/></svg> Watch Stream</>
            }
          </a>
        )}

        {/* Completed with recording → inline player toggle */}
        {isCompleted && hasRecording && (
          <button
            onClick={() => setShowPlayer(p => !p)}
            style={{
              display:     'inline-flex',
              alignItems:  'center',
              gap:         8,
              padding:     '10px 20px',
              background:  showPlayer ? 'rgba(1,47,123,0.7)' : NAVY,
              border:      'none',
              borderRadius: 8,
              color:       '#fff',
              fontWeight:  800,
              fontSize:    14,
              cursor:      'pointer',
              letterSpacing: '-0.2px',
              whiteSpace:  'nowrap' as const,
              touchAction: 'manipulation',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="white">
              <polygon points="3,2 12,7 3,12"/>
            </svg>
            {showPlayer ? 'Hide Replay' : 'Watch Replay'}
          </button>
        )}

        {/* Completed but no recording yet */}
        {isCompleted && !hasRecording && (
          <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>
            Replay uploading soon…
          </span>
        )}
      </div>

      {/* ── Inline video player ── */}
      {showPlayer && recordingUrl && (
        <div style={{ padding: '0 16px 16px' }}>
          <video
            src={mediaUrl(recordingUrl)}
            controls
            playsInline
            autoPlay
            style={{ width: '100%', borderRadius: 8, background: '#000', display: 'block', maxHeight: '70vw' }}
            onError={e => {
              const v = e.currentTarget;
              console.error('Video error:', v.error?.code, v.error?.message, recordingUrl);
            }}
          />
          <div style={{ marginTop: 8, fontSize: 11, color: 'rgba(255,255,255,0.22)', textAlign: 'center' }}>
            Full game recording · WVT 2032 vs. {game.opponent}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const WVT2032Watch: React.FC = () => (
  <div style={{
    minHeight:     '100vh',
    background:    BG,
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    fontFamily:    'system-ui, -apple-system, sans-serif',
  }}>

    {/* ── Hero ── */}
    <div style={{
      width:         '100%',
      background:    `linear-gradient(160deg, ${NAVY} 0%, #001450 55%, ${BG} 100%)`,
      borderBottom:  '1px solid rgba(255,255,255,0.07)',
      display:       'flex',
      flexDirection: 'column',
      alignItems:    'center',
      padding:       '36px 24px 32px',
      boxSizing:     'border-box',
      position:      'relative',
      overflow:      'hidden',
    }}>
      {/* Subtle grid overlay */}
      <div style={{
        position:        'absolute',
        inset:           0,
        pointerEvents:   'none',
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
        backgroundSize:  '40px 40px',
      }} />

      {/* Basketball icon + team name */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, position: 'relative' }}>
        <div style={{
          width: 64, height: 64, borderRadius: 12,
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 34,
        }}>
          🏀
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.45)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 3 }}>
            WVT Basketball
          </div>
          <div style={{ fontSize: 24, fontWeight: 900, color: '#fff', letterSpacing: '-0.5px', lineHeight: 1.1 }}>
            2032 McFarland
          </div>
        </div>
      </div>

      {/* Tournament pill */}
      <div style={{
        background:   'rgba(255,255,255,0.07)',
        border:       '1px solid rgba(255,255,255,0.14)',
        borderRadius: 40,
        padding:      '6px 18px',
        marginBottom: 10,
      }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: '#F97316', letterSpacing: '2px', textTransform: 'uppercase' }}>
          🏆 Summer Tournament · Hamilton, OH
        </span>
      </div>

      <p style={{ margin: 0, fontSize: 14, color: 'rgba(255,255,255,0.4)', textAlign: 'center', maxWidth: 360 }}>
        Live streaming · June 27–28, 2026
      </p>
    </div>

    {/* ── Game cards ── */}
    <div style={{
      width: '100%', maxWidth: 580,
      padding: '28px 20px 0',
      boxSizing: 'border-box',
      display: 'flex', flexDirection: 'column', gap: 14,
    }}>
      {/* Today header */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 2 }}>
        Today — Saturday, June 27
      </div>
      <GameCard key={GAMES[0].id} game={GAMES[0]} index={0} />
      <GameCard key={GAMES[1].id} game={GAMES[1]} index={1} />

      {/* Tomorrow header */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '1.5px', textTransform: 'uppercase', marginTop: 8, marginBottom: 2 }}>
        Sunday, June 28
      </div>
      <GameCard key={GAMES[2].id} game={GAMES[2]} index={2} />
    </div>

    {/* ── Footer CTAs ── */}
    <div style={{
      width: '100%', maxWidth: 580,
      padding: '32px 20px 44px',
      boxSizing: 'border-box',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20,
    }}>
      <div style={{ width: '100%', height: 1, background: 'rgba(255,255,255,0.06)' }} />

      <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.32)', textAlign: 'center' }}>
        Get the full experience — live scores, stats &amp; highlights
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center', alignItems: 'center' }}>
        <AppStoreBadge />
        <a
          href="https://www.slowrib.com/register"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 20px',
            background: 'rgba(249,115,22,0.10)', border: '1px solid rgba(249,115,22,0.35)',
            borderRadius: 8, color: '#F97316', fontWeight: 700, fontSize: 14,
            textDecoration: 'none', whiteSpace: 'nowrap' as const,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="8" cy="5" r="3"/>
            <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"/>
          </svg>
          Create Free Account
        </a>
      </div>

      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.16)', textAlign: 'center', lineHeight: 1.7 }}>
        Powered by{' '}
        <a href="https://www.slowrib.com" style={{ color: 'rgba(255,255,255,0.28)', textDecoration: 'none', fontWeight: 600 }}>
          Slow Rib Sports
        </a>
        {' · '}
        <a href="https://www.slowrib.com/privacy" style={{ color: 'rgba(255,255,255,0.18)', textDecoration: 'none' }}>Privacy</a>
        {' · '}
        <a href="https://www.slowrib.com/terms" style={{ color: 'rgba(255,255,255,0.18)', textDecoration: 'none' }}>Terms</a>
      </div>
    </div>

    <style>{`
      @keyframes wvt-pulse {
        0%, 100% { opacity: 1; }
        50%       { opacity: 0.3; }
      }
    `}</style>
  </div>
);

export default WVT2032Watch;
